import { useAuthentication, useUser } from "@credit-acceptance/common-frontend";
import type { UserWithCarLots } from "@credit-acceptance/common-types";

export interface UseAuthResult {
  isAuthenticated: boolean;
  isLoading: boolean;
  user?: UserWithCarLots;
  error?: string;
}

/**
 * Matches the page-hosting hostnames where oauth2-proxy is wired
 * (uilab/uidev/uitest/uiqa/ui.creditacceptance.com). Mirrors the host
 * guard inside `checkAuthentication` from `@credit-acceptance/common-frontend`.
 */
const PAGE_HOSTING_HOST_REGEXP = /^ui\w*\.creditacceptance\.com$/;

/**
 * @returns {boolean} true when the current page is served from a page-hosting
 * origin and the real oauth2-proxy / CAPS session path should run. Anywhere
 * else (vite dev server, `pnpm preview`, Playwright preview server) the dev
 * branch is selected so MSW or a Playwright route stub can drive auth.
 */
function isPageHostingHost(): boolean {
  if (globalThis.location === undefined) {
    return false;
  }
  return PAGE_HOSTING_HOST_REGEXP.test(globalThis.location.host);
}

/**
 * Dev variant. The OAuth check is a no-op off page-hosting hosts
 * (`checkAuthentication` short-circuits there), so this path skips it
 * entirely and treats whichever user MSW or a Playwright stub supplies
 * as authenticated. Only `useUser` runs — no doubled subscription
 * through `useAuthentication`.
 * @returns {UseAuthResult} Authentication derived from the stubbed user.
 */
function useDevelopmentAuth(): UseAuthResult {
  const { user, isLoading, error } = useUser({ enabled: true });
  return {
    isAuthenticated: !!user,
    isLoading,
    user,
    error,
  };
}

/**
 * Prod variant. Runs the real oauth2-proxy session check via
 * `useAuthentication`, then fetches the CAPS user only once OAuth has
 * passed so we never hit `/steps/rest/user` against an unauthenticated
 * session.
 * @returns {UseAuthResult} Combined OAuth + CAPS user state.
 */
function useProductionAuth(): UseAuthResult {
  const { isAuthenticated, isLoading: authLoading } = useAuthentication();
  const {
    user,
    isLoading: userLoading,
    error,
  } = useUser({ enabled: isAuthenticated });

  return {
    isAuthenticated,
    isLoading: authLoading || (isAuthenticated && userLoading),
    user,
    error,
  };
}

/**
 * Single auth contract for the workbench app. The split is driven by
 * runtime host detection rather than `import.meta.env.DEV` so that the
 * preview-server-backed Playwright tests (which serve the prod bundle
 * from localhost:4173) still route through the dev branch. In real
 * page-hosting deployments the host check is true and the production
 * oauth2-proxy / CAPS flow runs.
 * @returns {UseAuthResult} Authentication status, loading state, optional user, and optional error.
 */
export function useAuth(): UseAuthResult {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return isPageHostingHost() ? useProductionAuth() : useDevelopmentAuth();
}
